/** API helpers pra Jobs / Dispatch / Invoices (F3-F5). */
import { api } from "@/lib/api";
import type { Page } from "@/lib/api-cadastros";

export type JobStatus =
  | "draft"
  | "open"
  | "dispatched"
  | "assigned"
  | "in_progress"
  | "completed"
  | "approved"
  | "unproductive"
  | "cancelled"
  | "expired";

export type JobUrgency = "normal" | "urgent" | "same_day";

export interface Job {
  id: string;
  code: string;
  client_id: string;
  contract_id: string | null;
  category_id: string | null;
  address_id: string | null;
  assigned_nerd_id: string | null;
  title: string;
  description: string | null;
  internal_notes: string | null;
  requester_name: string | null;
  requester_phone: string | null;
  status: JobStatus;
  urgency: JobUrgency;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  deadline_at: string | null;
  price: number;
  nerd_payout: number;
  km_distance: number;
  km_cost: number;
  extra_hours: number;
  extra_hours_cost: number;
  materials_cost: number;
  cancellation_fee: number;
  cancel_reason: string | null;
  unproductive_reason: string | null;
  is_billable: boolean;
  lat: number | null;
  lng: number | null;
  created_at: string;
  updated_at: string;
}

export const jobsApi = {
  list: async (params: Record<string, unknown> = {}): Promise<Page<Job>> => {
    const { data } = await api.get<Page<Job>>("/v1/jobs", { params });
    return data;
  },
  get: async (id: string): Promise<Job> => {
    const { data } = await api.get<Job>(`/v1/jobs/${id}`);
    return data;
  },
  create: async (body: Partial<Job>): Promise<Job> => {
    const { data } = await api.post<Job>("/v1/jobs", body);
    return data;
  },
  update: async (id: string, body: Partial<Job>): Promise<Job> => {
    const { data } = await api.patch<Job>(`/v1/jobs/${id}`, body);
    return data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/v1/jobs/${id}`);
  },
  transition: async (id: string, status: JobStatus, reason?: string): Promise<Job> => {
    const { data } = await api.post<Job>(`/v1/jobs/${id}/transition`, { status, reason });
    return data;
  },
};

// ── Dispatch ─────────────────────────────────────────────────────────────
export type BroadcastStatus = "pending" | "dispatching" | "completed" | "exhausted" | "cancelled";

export interface Broadcast {
  id: string;
  job_id: string;
  batch_number: number;
  radius_km: number;
  status: BroadcastStatus;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
  notes: string | null;
  created_at: string;
}

// ── Invoicing ────────────────────────────────────────────────────────────
export type InvoiceStatus =
  | "awaiting_nf"
  | "reviewing"
  | "scheduled"
  | "pending_fix"
  | "rejected"
  | "paid"
  | "cancelled";

export interface NerdInvoice {
  id: string;
  code: string;
  nerd_id: string;
  payment_window_id: string | null;
  status: InvoiceStatus;
  contract_type: string;
  invoice_date: string | null;
  scheduled_payment_date: string | null;
  paid_at: string | null;
  services_total: number;
  reimbursement_total: number;
  total: number;
  advance_requested: boolean;
  advance_processed: boolean;
  advance_fee_amount: number | null;
  nf_validated: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentWindow {
  id: string;
  year: number;
  month: number;
  service_start: string;
  service_end: string;
  nf_deadline: string;
  payment_date: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ShortlistEntry {
  id: string;
  broadcast_id: string;
  nerd_id: string;
  match_score: number;
  distance_km: number;
  rank_position: number;
  notified_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  created_at: string;
}

export const dispatchApi = {
  listBroadcasts: async (params: Record<string, unknown> = {}): Promise<Page<Broadcast>> => {
    const { data } = await api.get<Page<Broadcast>>("/v1/dispatch/broadcasts", { params });
    return data;
  },
  createBroadcast: async (body: { job_id: string; radius_km?: number; expires_in_minutes?: number }): Promise<Broadcast> => {
    const { data } = await api.post<Broadcast>("/v1/dispatch/broadcasts", body);
    return data;
  },
  getShortlist: async (broadcast_id: string): Promise<Page<ShortlistEntry>> => {
    const { data } = await api.get<Page<ShortlistEntry>>(`/v1/dispatch/shortlist/${broadcast_id}`);
    return data;
  },
};

export const invoicesApi = {
  list: async (params: Record<string, unknown> = {}): Promise<Page<NerdInvoice>> => {
    const { data } = await api.get<Page<NerdInvoice>>("/v1/invoices", { params });
    return data;
  },
  get: async (id: string): Promise<NerdInvoice> => {
    const { data } = await api.get<NerdInvoice>(`/v1/invoices/${id}`);
    return data;
  },
  create: async (body: { nerd_id: string; contract_type: string; job_ids: string[] }): Promise<NerdInvoice> => {
    const { data } = await api.post<NerdInvoice>("/v1/invoices", body);
    return data;
  },
  update: async (id: string, body: Partial<NerdInvoice>): Promise<NerdInvoice> => {
    const { data } = await api.patch<NerdInvoice>(`/v1/invoices/${id}`, body);
    return data;
  },
  approve: async (id: string): Promise<NerdInvoice> => {
    const { data } = await api.post<NerdInvoice>(`/v1/invoices/${id}/approve`);
    return data;
  },
  requestAdvance: async (id: string): Promise<NerdInvoice> => {
    const { data } = await api.post<NerdInvoice>(`/v1/invoices/${id}/request-advance`);
    return data;
  },
  approveAdvance: async (id: string): Promise<NerdInvoice> => {
    const { data } = await api.post<NerdInvoice>(`/v1/invoices/${id}/approve-advance`);
    return data;
  },
  markPaid: async (id: string): Promise<NerdInvoice> => {
    const { data } = await api.post<NerdInvoice>(`/v1/invoices/${id}/mark-paid`);
    return data;
  },
  generateMonth: async (year: number, month: number, send = false): Promise<Record<string, unknown>> => {
    const { data } = await api.post("/v1/invoices/generate-month", { year, month, send });
    return data;
  },
  nfLink: async (id: string): Promise<{ url: string }> => {
    const { data } = await api.get<{ url: string }>(`/v1/invoices/${id}/nf-link`);
    return data;
  },
};

export const paymentWindowsApi = {
  list: async (params: Record<string, unknown> = {}): Promise<Page<PaymentWindow>> => {
    const { data } = await api.get<Page<PaymentWindow>>("/v1/payment-windows", { params });
    return data;
  },
  create: async (body: Partial<PaymentWindow>): Promise<PaymentWindow> => {
    const { data } = await api.post<PaymentWindow>("/v1/payment-windows", body);
    return data;
  },
  update: async (id: string, body: Partial<PaymentWindow>): Promise<PaymentWindow> => {
    const { data } = await api.patch<PaymentWindow>(`/v1/payment-windows/${id}`, body);
    return data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/v1/payment-windows/${id}`);
  },
};
